import { ref, onMounted, computed } from 'vue';
import type { Route } from '@/domains/route/types/Route';
import type { ApiResponse } from '@/types/api';
import {
  clearRoutesCache,
  updateRoute,
  getAllRoutesFromCache,
  observeAllRoutes,
  upsertRoutes,
  type RouteUpdate,
} from '@/domains/route/repositories/routeRepository';
import { useRouteStore } from '@/domains/route/stores/useRouteStore';
import { useApiQuery } from '@/composables/useApi';
import { routesEndpoints } from '@/services/api/apiConfig';
import { useAutoUnsubscribe } from '@/composables/useAutoUnsubscribe';
import { useConstants } from '@/domains/constants';
import { useDateFormat } from '@/composables/useDateFormat';

const routes = ref<Route[] | null>(null);
const error = ref<Error | null>(null);
const isStartingRoute = ref(false);
const isCompletingRoute = ref(false);

export const useRoutes = () => {
  // Dependencies
  const { trackSubscription } = useAutoUnsubscribe();
  const routeStore = useRouteStore();
  const { actions: constantsActions } = useConstants();
  const { actions: dateActions } = useDateFormat();

  // computed route status constants
  const startedRouteStatus = computed(() => constantsActions.getRouteStatusByCode('STD'));
  const completedRouteStatus = computed(() => constantsActions.getRouteStatusByCode('CMP'));

  const routesQuery = useApiQuery<ApiResponse<Route[]>, Error>({
    endpoint: routesEndpoints.LIST(),
    enabled: false, // Only fetch when explicitly called via refetch
    queryKey: ['routes'],
  });

  // === BUSINESS LOGIC ===

  // Infrastructure helpers
  const fetchRoutesFromApi = async (): Promise<Route[]> => {
    const result = await routesQuery.refetch();
    const response = result.data;
    if (!response?.data) {
      throw new Error('No route data available in API response');
    }
    return response.data;
  };

  // Fetches broker routes by syncing from API first, falls back to cache if API fails
  // Always attempts to sync from API when called
  // - If API responds: syncs with intelligent date comparison and returns updated routes
  // - If API fails: falls back to cache and returns cached routes

  const loadRoutes = async (): Promise<void> => {
    if (routesQuery.isLoading.value) {
      return;
    }

    error.value = null;

    try {
      const routesData = await fetchRoutesFromApi();
      const updatedRoutes = await upsertRoutes(routesData);
      routes.value = updatedRoutes;
    } catch (e) {
      console.warn('[useRoutes] API sync failed, falling back to cache:', e);

      const cachedRoutes = await getAllRoutesFromCache();

      if (cachedRoutes && cachedRoutes.length > 0) {
        routes.value = cachedRoutes;
        error.value = null;
        return;
      }

      error.value = e instanceof Error ? e : new Error('Failed to fetch broker routes');

      throw e;
    }
  };

  const startRoute = async (routeId: number): Promise<void> => {
    const now = dateActions.utcNowDate();
    const changes: RouteUpdate = {
      updatedDate: now,
      startedOn: now,
      syncStatus: 'pending',
      statusCodeId: startedRouteStatus.value.id,
    };

    await updateRoute(routeId, changes);
  };

  const completeRoute = async (routeId: number): Promise<void> => {
    const now = dateActions.utcNowDate();
    const changes: RouteUpdate = {
      updatedDate: now,
      syncStatus: 'pending',
      statusCodeId: completedRouteStatus.value.id,
    };

    await updateRoute(routeId, changes);
  };

  const startRouteById = async (routeId: number) => {
    if (!routeId || routeId <= 0) {
      throw new Error('Invalid routeId: routeId must be a positive number');
    }

    if (isStartingRoute.value) {
      return;
    }

    isStartingRoute.value = true;
    try {
      await startRoute(routeId);
    } finally {
      isStartingRoute.value = false;
    }
  };

  const completeRouteById = async (routeId: number) => {
    if (!routeId || routeId <= 0) {
      throw new Error('Invalid routeId: routeId must be a positive number');
    }

    if (isCompletingRoute.value) {
      return;
    }

    isCompletingRoute.value = true;
    try {
      await completeRoute(routeId);
    } finally {
      isCompletingRoute.value = false;
    }
  };

  const clearRoutes = async () => {
    await clearRoutesCache();
    routes.value = [];
  };

  const selectRoute = (routeId: number) => {
    routeStore.setSelectedRoute(routeId);
  };

  // Business Selectors
  const getRouteById = (routeId: number): Route | null => {
    return (routes.value ?? []).find((route) => route.id === routeId) ?? null;
  };

  // === LIFECYCLE ===

  onMounted(() => {
    trackSubscription(
      observeAllRoutes().subscribe({
        next: (routeRows: Route[]) => {
          routes.value = routeRows ?? [];
        },
        error: () => {
          // no-op: keep previous state
        },
      })
    );
  });

  // === COMPOSABLE OUTPUT ===

  const state = { routes };

  const selectors = {
    getRouteById,
  };

  const actions = {
    loadRoutes,
    clearRoutes,
    startRouteById,
    completeRouteById,
    selectRoute,
  };

  const status = {
    isLoading: computed(() => routesQuery.isLoading.value),
    error: computed(() => error.value || routesQuery.error.value),
    isStartingRoute: computed(() => isStartingRoute.value),
    isCompletingRoute: computed(() => isCompletingRoute.value),
  };

  return {
    state,
    selectors,
    actions,
    status,
  };
};
